import { TemplateElement, html, defineElement, when } from '@webtides/element-js';
import { whenConnected } from '../../utils/custom-elements.js';
import style from './page-legend.css';
import '../icon/icon.js';

/**
 * @property {string} title
 * @property {string} containerSelector
 * @property {string} headlinesSelector
 */
export default class PageLegend extends TemplateElement {
    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            title: '',
            containerSelector: 'main',
            headlinesSelector: 'h2, h3, h4, h5, h6',
            currentHeadline: undefined,
            headlines: [],
        };
    }

    connected() {
        whenConnected('mb-rich-text').then(() => {
            const headlines = document.querySelector(this.containerSelector).querySelectorAll(this.headlinesSelector);
            if (headlines.length > 0) {
                this.headlines = this.parseHeadlines(headlines);
                this.currentHeadline = this.headlines[0].id;
            }
        });
    }

    events() {
        return {
            window: {
                hashchange: () => {
                    this.currentHeadline = location.hash.replace('#', '');
                },
            },
        };
    }

    parseHeadlines(headlines) {
        const firstHeadlineLevel = parseInt(headlines[0].tagName.substring(1));
        const root = { headlines: [] };
        let currentLevel = [root];

        headlines.forEach((headline) => {
            const level = parseInt(headline.tagName.substring(1));
            const headlineTitle = headline.textContent.trim();

            if (headlineTitle === '') {
                return;
            }

            if (!headline.id) {
                headline.id = encodeURIComponent(headlineTitle);
            }

            const newItem = { title: headlineTitle, id: headline.id, headlines: [] };

            currentLevel[level - firstHeadlineLevel].headlines.push(newItem);
            currentLevel[level - firstHeadlineLevel + 1] = newItem;
        });

        return root.headlines;
    }

    template() {
        return html`
            ${when(
                this.headlines.length > 0,
                html`
                    <nav aria-labelledby="on-this-page-title" class="w-full">
                        <h2 id="on-this-page-title" class="mb-32">${this.title}</h2>
                        <ol class="flex flex-col gap-16 mb-0">
                            ${this.headlines.map(
                                (headline) => html`
                                    <li>
                                        ${this.renderLink(headline)}
                                        ${when(
                                            headline.headlines.length > 0,
                                            html`
                                                <ol class="flex flex-col gap-4 ml-2 mt-2 mb-0">
                                                    ${headline.headlines.map(
                                                        (nestedHeadline) => html`
                                                            <li>${this.renderLink(nestedHeadline)}</li>
                                                        `,
                                                    )}
                                                </ol>
                                            `,
                                        )}
                                    </li>
                                `,
                            )}
                        </ol>
                    </nav>
                `,
            )}
        `;
    }

    renderLink(headline) {
        return html`
            <a
                class="flex items-center gap-8 text-gray-50 underline ${when(
                    this.currentHeadline === headline.id,
                    'is-current',
                    '',
                )}"
                href="#${headline.id}"
            >
                <mb-icon name="navigation--cta-arrow-right" class="h-12 w-12 text-primary"></mb-icon>
                <span>${headline.title}</span>
            </a>
        `;
    }
}

defineElement('mb-page-legend', PageLegend);
